import { FC, Fragment, useContext, useState } from 'react';
import { MoContext } from 'contexts/MoContext';
import { actionTargetsOrder, useOrdersAction } from '../Assistant/ordersActions';
import { useTranslation } from 'react-i18next';
import { OrderRow, FullSegment } from 'ts/shared/interfaces/MOResponse';
import { print } from 'services/myOrders';
import { showSuccess } from 'utils/utils';
import Button from 'components/ui/Button/Button';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import Dropdown from '../../Dropdown';
import MoEmailModal from './MoEmailModal';
import withErrorBoundary from '../../withErrorBoundary';

interface MoPrintProps {
  segment: FullSegment;
  orderRow: OrderRow;
  toBottom?: boolean;
}

const PRINT_REPORTS = {
  information: ['OrderInformationReport', 'order_information'],
  public: ['OrderPublicReport', 'order_public_price'],
  agent: ['OrderAgentReport', 'agent'],
  operator: ['OrderOperatorReport', 'order_operator'],
} as const;

const MoPrint: FC<MoPrintProps> = ({ segment, orderRow, toBottom = false, ...props }) => {
  const [t] = useTranslation();
  const { activeOrder } = useContext(MoContext);
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [reportType, setReportType] = useState('');
  const [menuCloseDate, setMenuCloseDate] = useState<Date>();
  const isActive =
    activeOrder?.fullSegment.orderSegId === segment.orderSegId ||
    activeOrder?.orderRow.segmentId === orderRow.segmentId;

  const onClickPrint = async (orderReportType: string, fileName: string) => {
    setMenuCloseDate(new Date());
    setIsLoading(true);
    const response = await print({
      orderId: orderRow.orderId,
      segmentId: segment.orderSegId,
      orderReportType,
    });

    if (response) {
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName}#${orderRow.orderId}.pdf`); //or any other extension
      document.body.appendChild(link);
      link.click();

      showSuccess(t('mo.print_success'));
    }

    setReportType(orderReportType);
    setOpenModal(true);
    setIsLoading(false);
  };

  useOrdersAction(
    (action) =>
      action.action === 'print' &&
      actionTargetsOrder(
        action,
        {
          segmentId: orderRow.segmentId,
          altSegmentId: segment.orderSegId,
          orderId: orderRow.orderId,
          altOrderId: segment.orderId,
        },
        isActive,
      ),
    (action) => {
      if (action.report === 'email') {
        setReportType('OrderInformationReport');
        setOpenModal(true);
        return;
      }
      const report = PRINT_REPORTS[action.report || 'information'];
      void onClickPrint(report[0], report[1]);
    },
    isActive,
  );

  return (
    <Fragment>
      <Dropdown
        byClick={true}
        top={toBottom ? undefined : -180}
        bottom={toBottom ? -180 : undefined}
        right={-130}
        left={0}
        openerEmpty={true}
        closeOnClickDate={menuCloseDate}
        opener={
          <Button
            disabled={isLoading}
            title={t(isLoading ? 'mo.loading' : 'mo.print')}
            className="btn btn-green btn-ssm"
            icon={faPrint}
            iconclassname="mr-5"
          />
        }
      >
        <div
          className="c-dropdown-row text-sm"
          onClick={() => onClickPrint('OrderInformationReport', 'order_information')}
        >
          {t('mo.order_information')}
        </div>
        <div
          className="c-dropdown-row text-sm"
          onClick={() => onClickPrint('OrderPublicReport', 'order_public_price')}
        >
          {t('mo.order_public_price')}
        </div>
        <div className="c-dropdown-row text-sm">{t('mo.order_send_order_email')}</div>
        <div
          className="c-dropdown-row text-sm"
          onClick={() => onClickPrint('OrderAgentReport', 'agent')}
        >
          {t('mo.agent')}
        </div>
        <div
          className="c-dropdown-row text-sm"
          onClick={() => onClickPrint('OrderOperatorReport', 'order_operator')}
        >
          {t('mo.order_operator')}
        </div>
      </Dropdown>
      <MoEmailModal
        orderRow={orderRow}
        onClose={() => setOpenModal(false)}
        isOpen={openModal}
        reportType={reportType}
      />
    </Fragment>
  );
};

export default withErrorBoundary(MoPrint, 'MoPrint');
